import React, { useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import {
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import '@uiw/react-md-editor/markdown-editor.css';
import { IN_DEPTH_SECTION_LABELS } from '../utils/inDepthAnalysisSections';

const TEXTAREA_CLASS = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y';
const SMALL_FIELD_CLASS = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

export const TabStatusIcon = ({ status }) => {
  if (status === 'unsaved') return <span className="h-2 w-2 rounded-full bg-yellow-500" title="Unsaved changes" />;
  if (status === 'saved') return <CheckCircleIcon className="h-4 w-4 text-green-500" title="Saved" />;
  if (status === 'error') return <XCircleIcon className="h-4 w-4 text-red-500" title="Save error" />;
  if (status === 'saving') {
    return <div className="h-4 w-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" title="Saving..." />;
  }
  return null;
};

const LabeledTextarea = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      rows={8}
      className={TEXTAREA_CLASS}
      placeholder={placeholder}
    />
  </div>
);

const AT_GLANCE_FIELDS = [
  ['at_glance_summary', 'Summary', 'Enter session summary...'],
  ['at_glance_keypoints', 'Keypoints', 'Enter key points...'],
  ['at_glance_speakers', 'Speakers', 'Enter speaker information...'],
];

export const AtGlanceForm = ({ data, onChange }) => (
  <div className="space-y-6">
    {AT_GLANCE_FIELDS.map(([key, label, placeholder]) => (
      <LabeledTextarea
        key={key}
        label={label}
        value={data[key]}
        onChange={(value) => onChange(key, value)}
        placeholder={placeholder}
      />
    ))}
  </div>
);

/** Legacy flat markdown sections, for reports without a structured analysis dict. */
export const LegacyAnalysisForm = ({ sections, onChange }) => (
  <div className="space-y-6">
    {IN_DEPTH_SECTION_LABELS.map(({ key, label }) => (
      <LabeledTextarea
        key={key}
        label={label}
        value={sections?.[key]}
        onChange={(value) => onChange(key, value)}
        placeholder={`Enter ${label.toLowerCase()}...`}
      />
    ))}
  </div>
);

const CollapseToggle = ({ collapsed, onToggle, what, iconClass, buttonClass }) => (
  <button
    type="button"
    onClick={onToggle}
    className={buttonClass}
    title={collapsed ? `Expand ${what}` : `Collapse ${what}`}
  >
    {collapsed ? (
      <ChevronRightIcon className={iconClass} />
    ) : (
      <ChevronDownIcon className={iconClass} />
    )}
  </button>
);

const ArgumentCard = ({ argument, collapsed, onToggle, onUpdate, onRemove }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 flex-1">
        <CollapseToggle
          collapsed={collapsed}
          onToggle={onToggle}
          what="argument"
          buttonClass="p-1 rounded hover:bg-gray-100 transition-colors"
          iconClass="h-4 w-4 text-gray-600"
        />
        <input
          type="text"
          value={argument.name || ''}
          onChange={(e) => onUpdate('name', e.target.value)}
          placeholder="Argument name"
          className="flex-1 px-3 py-2 font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
      <button
        onClick={onRemove}
        className="ml-2 p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
        title="Remove argument"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>

    {!collapsed && (
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Explanation</label>
          <textarea
            value={argument.explanation || ''}
            onChange={(e) => onUpdate('explanation', e.target.value)}
            rows={3}
            className={`${SMALL_FIELD_CLASS} resize-y`}
            placeholder="Explain the argument..."
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Evidence</label>
          <textarea
            value={argument.evidence || ''}
            onChange={(e) => onUpdate('evidence', e.target.value)}
            rows={2}
            className={`${SMALL_FIELD_CLASS} resize-y`}
            placeholder="Supporting evidence..."
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Major Discussion Point</label>
          <input
            type="text"
            value={argument.majorDiscussionPoint || ''}
            onChange={(e) => onUpdate('majorDiscussionPoint', e.target.value)}
            className={SMALL_FIELD_CLASS}
            placeholder="Major discussion point..."
          />
        </div>
      </div>
    )}
  </div>
);

const SpeakerCard = ({ speaker, speakerIndex, collapsedArguments, onToggleArgument, collapsed, onToggle, handlers }) => (
  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2 flex-1">
        <CollapseToggle
          collapsed={collapsed}
          onToggle={onToggle}
          what="speaker"
          buttonClass="p-1 rounded hover:bg-gray-200 transition-colors"
          iconClass="h-5 w-5 text-gray-600"
        />
        <input
          type="text"
          value={speaker.name || ''}
          onChange={(e) => handlers.updateSpeaker(speakerIndex, 'name', e.target.value)}
          placeholder="Speaker name"
          className="flex-1 px-3 py-2 text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
        />
      </div>
      <button
        onClick={() => handlers.removeSpeaker(speakerIndex)}
        className="ml-3 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        title="Remove speaker"
      >
        <TrashIcon className="h-5 w-5" />
      </button>
    </div>

    {!collapsed && (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700">Arguments</h4>
          <button
            onClick={() => handlers.addArgument(speakerIndex)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <PlusIcon className="h-3 w-3" />
            Add Argument
          </button>
        </div>

        {(!speaker.arguments || speaker.arguments.length === 0) ? (
          <p className="text-sm text-gray-500 italic">No arguments added for this speaker.</p>
        ) : (
          <div className="space-y-3">
            {speaker.arguments.map((argument, argIndex) => {
              const argKey = `${speakerIndex}-${argIndex}`;
              return (
                <ArgumentCard
                  key={argIndex}
                  argument={argument}
                  collapsed={collapsedArguments[argKey] === true}
                  onToggle={() => onToggleArgument(argKey)}
                  onUpdate={(field, value) => handlers.updateArgument(speakerIndex, argIndex, field, value)}
                  onRemove={() => handlers.removeArgument(speakerIndex, argIndex)}
                />
              );
            })}
          </div>
        )}
      </div>
    )}
  </div>
);

/** Speakers with their arguments; `handlers` add, update and remove them. */
export const SpeakersForm = ({ speakers, handlers }) => {
  const [collapsedSpeakers, setCollapsedSpeakers] = useState({});
  const [collapsedArguments, setCollapsedArguments] = useState({});
  const toggle = (setter, key) => setter((prev) => ({ ...prev, [key]: !(prev[key] === true) }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Add speakers and their arguments. Each argument can have explanation, evidence, and discussion points.
        </p>
        <button
          onClick={handlers.addSpeaker}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Add Speaker
        </button>
      </div>

      {(!speakers || speakers.length === 0) ? (
        <div className="text-center py-12 text-gray-500">
          <p>No speakers added yet. Click "Add Speaker" to get started.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {speakers.map((speaker, speakerIndex) => (
            <SpeakerCard
              key={speakerIndex}
              speaker={speaker}
              speakerIndex={speakerIndex}
              collapsed={collapsedSpeakers[speakerIndex] === true}
              onToggle={() => toggle(setCollapsedSpeakers, speakerIndex)}
              collapsedArguments={collapsedArguments}
              onToggleArgument={(argKey) => toggle(setCollapsedArguments, argKey)}
              handlers={handlers}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PREVIEW_MODES = [
  ['edit', 'Edit'],
  ['live', 'Live Preview'],
  ['preview', 'Preview'],
];

/** Markdown editor with its Edit / Live Preview / Preview switch. */
export const MarkdownSectionEditor = ({ tabId, value, onChange }) => {
  const [previewMode, setPreviewMode] = useState('edit');
  return (
    <>
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">View Mode:</span>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {PREVIEW_MODES.map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => setPreviewMode(mode)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  previewMode === mode
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <MDEditor
        key={`${tabId}-${previewMode}`}
        value={value || ''}
        onChange={onChange}
        preview={previewMode}
        hideToolbar={false}
        visibleDragBar={true}
        height={600}
        data-color-mode="light"
      />
    </>
  );
};
